import _ from 'lodash'
import addressSearchUtils from '@/utils/addressSearch/addressSearchUtils'

// All services are open data without an API key and allow cross-origin requests from the browser.
// The public Photon server (OpenStreetMap) is fair use only; a busy installation should run its own.
const VLAANDEREN_GEOLOCATION_URL = 'https://geo.api.vlaanderen.be/geolocation/v4'
const VLAANDEREN_CAPAKEY_URL = 'https://geo.api.vlaanderen.be/capakey/v2'
const PDOK_LOCATIESERVER_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1'
const GEOPLATEFORME_GEOCODING_URL = 'https://data.geopf.fr/geocodage'
const CARTOCIUDAD_GEOCODER_URL = 'https://www.cartociudad.es/geocoder/api/geocoder'
const PHOTON_URL = 'https://photon.komoot.io/api/'
// Keeps OpenStreetMap results in Europe, so a search does not end up at an American namesake.
const EUROPE_BBOX = '-25,34,45,72'
const PHOTON_LAYERS = ['house', 'street', 'locality', 'district', 'city']

const MAX_SUGGESTIONS = 8

export const SUGGESTION_TYPES = {
  ADDRESS: 'address',
  STREET: 'street',
  PLACE: 'place'
}

export const PROJECT_ADDRESS_FIELDS = {
  name: 'PrLocName',
  street: 'PrLocStreet',
  houseNumber: 'PrLocStreetNumber',
  postcode: 'PrLocZipPostalCode',
  place: 'PrLocCity',
  municipality: 'PrLocMunicipality',
  province: 'PrLocStateProvince'
}

async function getJson (url) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Address search request failed with status ${response.status}`)
  }
  return response.json()
}

// lang=default gives the local names (Torino, Italia) instead of the browser language (Turin, Italy).
function searchPhoton (query, limit) {
  const layers = PHOTON_LAYERS.map((layer) => `&layer=${layer}`).join('')
  return getJson(`${PHOTON_URL}?q=${encodeURIComponent(query)}&limit=${limit}&lang=default&bbox=${EUROPE_BBOX}${layers}`)
}

/**
 * The register gives the deelgemeente (e.g. Grembergen within Dendermonde) only through the
 * cadastral department of the parcel at the address position. The municipality when it has no
 * deelgemeenten, and null when there is no parcel there, as for the middle of a street.
 */
async function getVlaanderenPlace (x, y, municipality) {
  try {
    const parcel = await getJson(`${VLAANDEREN_CAPAKEY_URL}/parcel?x=${x}&y=${y}`)
    return addressSearchUtils.getPlaceFromCadastralDepartment(parcel.departmentName) || municipality
  } catch (error) {
    return null
  }
}

function getVlaanderenSuggestionType (label) {
  const firstPart = label.split(',')[0]
  if (/\d/.test(firstPart)) return SUGGESTION_TYPES.ADDRESS
  return label.includes(',') ? SUGGESTION_TYPES.STREET : SUGGESTION_TYPES.PLACE
}

const PDOK_SUGGESTION_TYPES = {
  adres: SUGGESTION_TYPES.ADDRESS,
  weg: SUGGESTION_TYPES.STREET,
  woonplaats: SUGGESTION_TYPES.PLACE
}

const GEOPLATEFORME_SUGGESTION_TYPES = {
  housenumber: SUGGESTION_TYPES.ADDRESS,
  street: SUGGESTION_TYPES.STREET,
  locality: SUGGESTION_TYPES.PLACE,
  municipality: SUGGESTION_TYPES.PLACE
}

const CARTOCIUDAD_SUGGESTION_TYPES = {
  portal: SUGGESTION_TYPES.ADDRESS,
  callejero: SUGGESTION_TYPES.STREET,
  carretera: SUGGESTION_TYPES.STREET,
  Municipio: SUGGESTION_TYPES.PLACE,
  poblacion: SUGGESTION_TYPES.PLACE
}

const PHOTON_SUGGESTION_TYPES = {
  house: SUGGESTION_TYPES.ADDRESS,
  street: SUGGESTION_TYPES.STREET,
  locality: SUGGESTION_TYPES.PLACE,
  district: SUGGESTION_TYPES.PLACE,
  city: SUGGESTION_TYPES.PLACE
}

export const PROVIDERS = {
  vlaanderen: {
    async suggest (query) {
      const data = await getJson(`${VLAANDEREN_GEOLOCATION_URL}/Suggestion?q=${encodeURIComponent(query)}&c=${MAX_SUGGESTIONS}`)
      return (data.SuggestionResult || []).map((label) => ({ id: label, label, type: getVlaanderenSuggestionType(label) }))
    },
    async resolve (suggestion) {
      const data = await getJson(`${VLAANDEREN_GEOLOCATION_URL}/Location?q=${encodeURIComponent(suggestion.label)}&c=1`)
      const location = (data.LocationResult || [])[0]
      if (!location) {
        throw new Error(`Address '${suggestion.label}' not found`)
      }
      const position = location.Location
      const precision = location.Housenumber ? SUGGESTION_TYPES.ADDRESS : location.Thoroughfarename ? SUGGESTION_TYPES.STREET : SUGGESTION_TYPES.PLACE
      const municipality = location.Municipality || ''
      // Without a parcel the deelgemeente is unknown. Plaats is then left alone rather than guessed,
      // because the municipality would overwrite a correct deelgemeente.
      const place = precision === SUGGESTION_TYPES.PLACE
        ? municipality
        : await getVlaanderenPlace(position.X_Lambert72, position.Y_Lambert72, municipality)
      return {
        street: location.Thoroughfarename || '',
        houseNumber: location.Housenumber || '',
        postcode: location.Zipcode || '',
        place: place || '',
        municipality,
        province: addressSearchUtils.getBelgianProvince(location.Zipcode),
        precision
      }
    }
  },

  pdok: {
    async suggest (query) {
      const filter = encodeURIComponent('type:(adres OR weg OR woonplaats)')
      const data = await getJson(`${PDOK_LOCATIESERVER_URL}/suggest?q=${encodeURIComponent(query)}&rows=${MAX_SUGGESTIONS}&fq=${filter}`)
      return ((data.response && data.response.docs) || [])
        .map((doc) => ({ id: doc.id, label: doc.weergavenaam, type: PDOK_SUGGESTION_TYPES[doc.type] }))
        .filter((suggestion) => suggestion.type)
    },
    async resolve (suggestion) {
      const fields = 'type,straatnaam,huis_nlt,postcode,woonplaatsnaam,gemeentenaam,provincienaam'
      const data = await getJson(`${PDOK_LOCATIESERVER_URL}/lookup?id=${encodeURIComponent(suggestion.id)}&fl=${fields}`)
      const doc = data.response && data.response.docs && data.response.docs[0]
      if (!doc) {
        throw new Error(`Address '${suggestion.label}' not found`)
      }
      return {
        street: doc.straatnaam || '',
        houseNumber: doc.huis_nlt || '',
        postcode: addressSearchUtils.formatDutchPostcode(doc.postcode),
        place: doc.woonplaatsnaam || '',
        municipality: doc.gemeentenaam || '',
        province: doc.provincienaam || '',
        precision: PDOK_SUGGESTION_TYPES[doc.type] || SUGGESTION_TYPES.PLACE
      }
    }
  },

  // Base Adresse Nationale through the Géoplateforme of IGN (the old api-adresse.data.gouv.fr is retired).
  france: {
    async suggest (query) {
      const data = await getJson(`${GEOPLATEFORME_GEOCODING_URL}/search?q=${encodeURIComponent(query)}&autocomplete=1&index=address&limit=${MAX_SUGGESTIONS}`)
      return (data.features || [])
        .map((feature) => ({ id: feature.properties.id, label: feature.properties.label, type: GEOPLATEFORME_SUGGESTION_TYPES[feature.properties.type], feature }))
        .filter((suggestion) => suggestion.type)
    },
    async resolve (suggestion) {
      const { properties } = suggestion.feature
      const precision = GEOPLATEFORME_SUGGESTION_TYPES[properties.type]
      return {
        street: properties.street || (precision === SUGGESTION_TYPES.STREET ? properties.name : ''),
        houseNumber: properties.housenumber || '',
        postcode: properties.postcode || '',
        place: properties.type === 'locality' ? properties.name : properties.city || '',
        municipality: properties.city || '',
        // context is 'département number, département, région'
        province: (properties.context || '').split(', ')[1] || '',
        precision
      }
    }
  },

  spain: {
    async suggest (query) {
      const candidates = await getJson(`${CARTOCIUDAD_GEOCODER_URL}/candidates?q=${encodeURIComponent(query)}&limit=${MAX_SUGGESTIONS}`)
      return (candidates || [])
        .map((candidate) => ({
          id: `${candidate.type}:${candidate.id}`,
          label: addressSearchUtils.formatCartoCiudadAddress(candidate.address),
          type: CARTOCIUDAD_SUGGESTION_TYPES[candidate.type],
          candidate
        }))
        .filter((suggestion) => suggestion.type)
    },
    async resolve (suggestion) {
      const candidate = suggestion.candidate
      const precision = CARTOCIUDAD_SUGGESTION_TYPES[candidate.type]
      return {
        street: precision === SUGGESTION_TYPES.PLACE ? '' : addressSearchUtils.getCartoCiudadStreet(candidate.address, candidate.portalNumber),
        houseNumber: candidate.portalNumber ? String(candidate.portalNumber) : '',
        postcode: candidate.postalCode || '',
        place: candidate.poblacion || candidate.muni || '',
        municipality: candidate.muni || '',
        province: candidate.province || '',
        precision
      }
    }
  },

  // Everything else: OpenStreetMap through Photon, the geocoder built for search-as-you-type. Where
  // a register knows better (see REGISTERS_BY_COUNTRY), searches and addresses come from there.
  europe: {
    async suggest (query) {
      const register = Object.values(REGISTERS_BY_COUNTRY).find((r) => r.postcodePattern.test(query))
      if (register) {
        const suggestions = await PROVIDERS[register.providerId].suggest(query)
        // Four digits need not be a Belgian postcode; without a match OpenStreetMap searches after all.
        if (suggestions.length) return suggestions.map((suggestion) => ({ ...suggestion, providerId: register.providerId }))
      }
      const data = await searchPhoton(query, MAX_SUGGESTIONS)
      return _.uniqBy((data.features || [])
        .map((feature) => ({
          id: `${feature.properties.osm_type}${feature.properties.osm_id}`,
          label: addressSearchUtils.getPhotonLabel(feature.properties),
          type: PHOTON_SUGGESTION_TYPES[feature.properties.type],
          feature
        }))
        .filter((suggestion) => suggestion.type), 'label')
    },
    async resolve (suggestion) {
      const { properties } = suggestion.feature
      const precision = PHOTON_SUGGESTION_TYPES[properties.type]
      const register = REGISTERS_BY_COUNTRY[properties.countrycode]
      if (register && register.precisions.includes(precision)) {
        const address = await findInRegister(register.providerId, properties, precision)
        if (address) return address
      }
      const isPlace = precision === SUGGESTION_TYPES.PLACE
      return {
        street: precision === SUGGESTION_TYPES.STREET ? properties.name : properties.street || '',
        houseNumber: properties.housenumber || '',
        postcode: properties.postcode || '',
        place: addressSearchUtils.getPhotonPlace(properties),
        // A Dutch city in OpenStreetMap is the woonplaats, not the gemeente.
        municipality: properties.countrycode === 'NL' ? '' : properties.city || (isPlace ? properties.name : ''),
        province: addressSearchUtils.getPhotonProvince(properties),
        precision
      }
    }
  }
}

/**
 * Registers that know what OpenStreetMap does not: searching on a postcode, the Dutch gemeente
 * (OpenStreetMap has only the woonplaats) and the Flemish deelgemeente (in OpenStreetMap only now
 * and then, as district). The Flemish register also covers Brussels, not Wallonia. A Belgian place
 * is already right in OpenStreetMap, where the register would give the merged municipality.
 * Dutch comes first, because a Dutch postcode holds four digits as well.
 */
const REGISTERS_BY_COUNTRY = {
  NL: {
    providerId: 'pdok',
    precisions: [SUGGESTION_TYPES.ADDRESS, SUGGESTION_TYPES.STREET, SUGGESTION_TYPES.PLACE],
    postcodePattern: /\b\d{4}\s?[a-z]{2}\b/i
  },
  BE: {
    providerId: 'vlaanderen',
    precisions: [SUGGESTION_TYPES.ADDRESS, SUGGESTION_TYPES.STREET],
    // Four digits; a house number has at most three.
    postcodePattern: /\b[1-9]\d{3}\b/
  }
}

/**
 * The register address of an OpenStreetMap result, or null when the register has no match of the
 * same precision and house number (or does not answer), in which case the OpenStreetMap data is used
 * after all.
 */
async function findInRegister (providerId, properties, precision) {
  const query = precision === SUGGESTION_TYPES.PLACE
    ? addressSearchUtils.getPhotonPlace(properties)
    : addressSearchUtils.formatAddressQuery({
      street: precision === SUGGESTION_TYPES.STREET ? properties.name : properties.street,
      houseNumber: properties.housenumber,
      postcode: properties.postcode,
      place: properties.city
    })
  if (!query) return null
  try {
    const [match] = await PROVIDERS[providerId].suggest(query)
    if (!match || match.type !== precision) return null
    if (precision === SUGGESTION_TYPES.ADDRESS && !addressSearchUtils.labelHasHouseNumber(match.label, properties.housenumber)) return null
    return { ...(await PROVIDERS[providerId].resolve(match)), providerId }
  } catch (error) {
    return null
  }
}

export const DEFAULT_PROVIDER_ID = 'europe'

export async function suggestAddresses (providerId, query) {
  const provider = PROVIDERS[providerId]
  const suggestions = await provider.suggest(query)
  const houseNumber = addressSearchUtils.getHouseNumber(query)
  if (!houseNumber || suggestions.some((s) => s.type === SUGGESTION_TYPES.ADDRESS && addressSearchUtils.labelHasHouseNumber(s.label, houseNumber))) {
    return suggestions
  }

  // The register does not know the typed number (new building, withdrawn number or typo), so the
  // street is offered with the number as typed.
  let street = suggestions.find((s) => s.type === SUGGESTION_TYPES.STREET)
  if (!street) {
    const withoutNumber = await provider.suggest(addressSearchUtils.removeHouseNumber(query))
    street = withoutNumber.find((s) => s.type === SUGGESTION_TYPES.STREET)
  }
  if (!street) return suggestions
  return [{ ...street, missingHouseNumber: houseNumber }, ...suggestions.filter((s) => s.id !== street.id)]
}

// Map zoom per precision of a located address: a house up close, a street with its surroundings, a
// place as a whole.
export const ADDRESS_ZOOM = {
  [SUGGESTION_TYPES.ADDRESS]: 18,
  [SUGGESTION_TYPES.STREET]: 16,
  [SUGGESTION_TYPES.PLACE]: 13
}

// The maps look the project address up every time they open, so a project is only asked for once.
const locatedAddresses = new Map()

export function getProjectAddressQuery (project) {
  if (!project) return ''
  const address = {}
  Object.keys(PROJECT_ADDRESS_FIELDS).forEach((key) => {
    address[key] = String(project[PROJECT_ADDRESS_FIELDS[key]] || '').trim()
  })
  return addressSearchUtils.formatAddressQuery(address)
}

/**
 * Where an address lies, through OpenStreetMap (Photon), which covers every country the address
 * search does. The project stores no coordinates for its address, so the maps look it up here.
 * @param {string} query e.g. from getProjectAddressQuery
 * @returns {Promise<{lonLat: number[], precision: string}|null>} null when nothing is found
 */
export async function locateAddress (query) {
  if (!query) return null
  if (locatedAddresses.has(query)) return locatedAddresses.get(query)
  try {
    const data = await searchPhoton(query, 1)
    const feature = (data.features || [])[0]
    const location = feature ? { lonLat: feature.geometry.coordinates, precision: PHOTON_SUGGESTION_TYPES[feature.properties.type] } : null
    locatedAddresses.set(query, location)
    return location
  } catch (error) {
    // Not remembered: the next time the map opens, the service may answer again.
    return null
  }
}

/**
 * @returns {Promise<object>} the address, with providerId the source it actually came from: a
 * Dutch address found through OpenStreetMap comes from the BAG
 */
export async function resolveAddress (providerId, suggestion) {
  const sourceId = suggestion.providerId || providerId
  const address = { providerId: sourceId, ...(await PROVIDERS[sourceId].resolve(suggestion)) }
  if (suggestion.missingHouseNumber) {
    address.houseNumber = suggestion.missingHouseNumber
    address.houseNumberNotInRegister = true
  }
  return address
}
